import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import eventoService from 'services/EventoService';
import userService from 'services/UserService';
import { message } from 'antd';

const EventoContext = createContext(null);

/**
 * Hook para acessar o contexto de evento
 * @returns {EventoContextValue}
 */
export const useEvento = () => {
  const context = useContext(EventoContext);
  if (!context) {
    throw new Error('useEvento deve ser usado dentro de um EventoProvider');
  }
  return context;
};

/**
 * Hook para reagir a mudanças de evento
 * @param {Function} callback - Função chamada quando o evento muda
 * @param {Array} deps - Dependências adicionais
 */
export const useEventoChange = (callback, deps = []) => {
  const { eventoAtivo } = useEvento();
  const isFirstRender = useRef(true);
  const previousEventoId = useRef(eventoAtivo?.id);

  useEffect(() => {
    // Não executar na primeira renderização
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousEventoId.current = eventoAtivo?.id;
      // Mas carregar dados iniciais se tiver evento
      if (eventoAtivo?.id) {
        callback(eventoAtivo);
      }
      return;
    }

    // Executar se o evento mudou
    if (previousEventoId.current !== eventoAtivo?.id) {
      previousEventoId.current = eventoAtivo?.id;
      if (eventoAtivo?.id) {
        callback(eventoAtivo);
      }
    }
  }, [eventoAtivo?.id, ...deps]);
};

/**
 * Provider do contexto de evento
 */
export const EventoProvider = ({ children }) => {
  const [eventoAtivo, setEventoAtivo] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = useSelector(state => state.auth.token);

  /**
   * Carrega a lista de eventos do usuário
   */
  const carregarEventos = useCallback(async () => {
    const currentUser = userService.getCurrentUser();
    if (!currentUser?.idUsuario) {
      setEventos([]);
      return [];
    }

    try {
      const eventosData = await eventoService.getEventosByUsuario(currentUser.idUsuario);
      setEventos(eventosData || []);
      return eventosData || [];
    } catch (err) {
      setError('Erro ao carregar eventos');
      setEventos([]);
      return [];
    }
  }, []);

  /**
   * Carrega o evento ativo salvo ou seleciona o primeiro disponível
   */
  const carregarEventoAtivo = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const currentUser = userService.getCurrentUser();
      if (!currentUser?.idUsuario) {
        setEventoAtivo(null);
        setLoading(false);
        return;
      }

      // STAFF não tem acesso ao endpoint de eventos por usuário;
      // o evento vinculado vem das claims do token (idEvento/apelidoEvento)
      const roles = userService.getCurrentUserRoles();
      const isStaff = roles.includes('STAFF') && !roles.includes('ADMIN') && !roles.includes('NOIVX');
      if (isStaff) {
        if (currentUser.idEvento) {
          const eventoStaff = {
            id: currentUser.idEvento,
            apelidoEvento: currentUser.apelidoEvento
          };
          eventoService.setEventoAtivo(eventoStaff);
          setEventoAtivo(eventoStaff);
          setEventos([eventoStaff]);
        } else {
          setEventoAtivo(null);
        }
        setLoading(false);
        return;
      }

      // Carregar lista de eventos
      const eventosData = await carregarEventos();

      // Tentar restaurar evento salvo
      const eventoAtivoId = eventoService.getEventoAtivoId();

      if (eventoAtivoId) {
        // Verificar se o evento salvo ainda existe na lista
        const eventoSalvo = eventosData.find(e => e.id === parseInt(eventoAtivoId));

        if (eventoSalvo) {
          setEventoAtivo(eventoSalvo);
        } else {
          // Evento salvo não existe mais, limpar e selecionar primeiro
          eventoService.clearEventoAtivo();
          if (eventosData.length > 0) {
            const primeiroEvento = eventosData[0];
            eventoService.setEventoAtivo(primeiroEvento);
            setEventoAtivo(primeiroEvento);
          }
        }
      } else if (eventosData.length > 0) {
        // Nenhum evento salvo, selecionar primeiro
        const primeiroEvento = eventosData[0];
        eventoService.setEventoAtivo(primeiroEvento);
        setEventoAtivo(primeiroEvento);
      }
    } catch (err) {
      setError('Erro ao carregar evento');
      eventoService.clearEventoAtivo();
      setEventoAtivo(null);
    } finally {
      setLoading(false);
    }
  }, [carregarEventos]);

  /**
   * Atualiza o evento ativo
   */
  const selecionarEvento = useCallback(async (evento) => {
    if (!evento?.id) {
      eventoService.clearEventoAtivo();
      setEventoAtivo(null);
      return;
    }

    try {
      eventoService.setEventoAtivo(evento);
      setEventoAtivo(evento);
    } catch (err) {
      message.error('Erro ao selecionar evento');
    }
  }, []);

  /**
   * Cria um novo evento
   */
  const criarEvento = useCallback(async (eventoData) => {
    const currentUser = userService.getCurrentUser();
    if (!currentUser?.idUsuario) {
      throw new Error('Usuário não autenticado');
    }

    const novoEvento = await eventoService.criarEvento(currentUser.idUsuario, eventoData);
    await carregarEventos();

    // Selecionar o novo evento automaticamente
    if (novoEvento?.id) {
      await selecionarEvento(novoEvento);
    }

    return novoEvento;
  }, [carregarEventos, selecionarEvento]);

  /**
   * Atualiza um evento existente
   */
  const atualizarEvento = useCallback(async (eventoId, eventoData) => {
    const eventoAtualizado = await eventoService.atualizarEvento(eventoId, eventoData);
    await carregarEventos();

    // Se o evento atualizado é o ativo, atualizar o estado
    if (eventoAtivo?.id === eventoId) {
      const eventosAtualizados = await eventoService.getEventosByUsuario(
        userService.getCurrentUser()?.idUsuario
      );
      const eventoNovo = eventosAtualizados?.find(e => e.id === eventoId);
      if (eventoNovo) {
        eventoService.setEventoAtivo(eventoNovo);
        setEventoAtivo(eventoNovo);
      }
    }

    return eventoAtualizado;
  }, [carregarEventos, eventoAtivo?.id]);

  /**
   * Exclui um evento
   */
  const excluirEvento = useCallback(async (eventoId) => {
    await eventoService.excluirEvento(eventoId);

    // Se o evento excluído é o ativo, limpar e selecionar outro
    if (eventoAtivo?.id === eventoId) {
      eventoService.clearEventoAtivo();
      setEventoAtivo(null);
    }

    // Recarregar lista e selecionar primeiro se necessário
    const eventosRestantes = await carregarEventos();
    if (eventoAtivo?.id === eventoId && eventosRestantes.length > 0) {
      const primeiroEvento = eventosRestantes[0];
      eventoService.setEventoAtivo(primeiroEvento);
      setEventoAtivo(primeiroEvento);
    }
  }, [carregarEventos, eventoAtivo?.id]);

  /**
   * Limpa o evento ativo (logout)
   */
  const limparEvento = useCallback(() => {
    eventoService.clearEventoAtivo();
    setEventoAtivo(null);
    setEventos([]);
  }, []);

  // Efeito para carregar evento quando o token muda
  useEffect(() => {
    if (token) {
      carregarEventoAtivo();
    } else {
      limparEvento();
      setLoading(false);
    }
  }, [token, carregarEventoAtivo, limparEvento]);

  const value = {
    // Estado
    eventoAtivo,
    eventos,
    loading,
    error,

    // Computed
    temEvento: !!eventoAtivo?.id,
    temEventos: eventos.length > 0,

    // Ações
    selecionarEvento,
    criarEvento,
    atualizarEvento,
    excluirEvento,
    carregarEventos,
    carregarEventoAtivo,
    limparEvento,
  };

  return (
    <EventoContext.Provider value={value}>
      {children}
    </EventoContext.Provider>
  );
};

export default EventoContext;
